import { useState } from "react";
import {
  Inbox as InboxIcon,
  GripVertical,
  type LucideIcon,
} from "lucide-react";
import { Inbox } from "./types";
import {
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/shadcn/sidebar";
import { cn } from "@/lib/utils";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { usePathname, useRouter } from "next/navigation";
import { WORKFLOW_ICONS } from "@/utils/consts";

interface InboxItemProps {
  inbox: Inbox;
  isActive: boolean;
  index: number;
  isAnyDragging: boolean;
  onDelete?: (inboxId: string) => void;
  badge?: string | number;
}

export const InboxItem = ({
  inbox,
  isActive,
  index,
  isAnyDragging,
  onDelete,
  badge,
}: InboxItemProps) => {
  const pathname = usePathname();
  const router = useRouter();

  const casesHref = `/inbox/${inbox.id}`;
  const isCasesActive = pathname === casesHref;

  const isCollapsible = inbox.meta?.is_collapsible;
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: inbox.id });

  const constrainedTransform = transform ? { ...transform, x: 0 } : transform;

  const style = {
    transform: CSS.Transform.toString(constrainedTransform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    willChange: isDragging ? "transform" : "auto",
    maxWidth: "100%",
  };

  const iconKey = (inbox.meta?.icon as string) || "inbox";
  const iconData =
    iconKey in WORKFLOW_ICONS
      ? WORKFLOW_ICONS[iconKey as keyof typeof WORKFLOW_ICONS]
      : null;
  const IconComponent = (iconData?.icon as LucideIcon) || InboxIcon;

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      style={style}
      className="list-none max-w-full"
    >
      <div className="flex items-center w-full group/inbox-item relative min-w-0 max-w-full">
        <SidebarMenuButton
          isActive={isActive}
          tooltip={inbox.name}
          onClick={(e) => {
            if (!isAnyDragging) {
              e.stopPropagation();
              router.push(casesHref);
            }
          }}
          className={cn(
            "flex-1 min-w-0 group/menu-button cursor-pointer relative",
            isAnyDragging && "pointer-events-none"
          )}
        >
          <div className="relative size-4 shrink-0">
            <div
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "absolute inset-0 cursor-grab active:cursor-grabbing transition-opacity z-10 flex items-center justify-center",
                !isAnyDragging
                  ? "opacity-0 group-hover/menu-button:opacity-100"
                  : "opacity-0"
              )}
            >
              <GripVertical className="size-4 text-sidebar-foreground/40" />
            </div>
            <IconComponent
              strokeWidth={1.25}
              className="size-4 shrink-0 text-sidebar-foreground group-data-[active=true]/menu-button:text-sidebar-accent-foreground group-hover/menu-button:text-sidebar-foreground transition-all group-hover/menu-button:opacity-0"
            />
          </div>
          <span className="pl-1 text-sm text-muted-foreground leading-tight truncate flex-1 min-w-0 group-data-[active=true]/menu-button:text-sidebar-accent-foreground group-data-[active=true]/menu-button:font-medium group-hover/menu-button:text-sidebar-foreground transition-colors">
            {inbox.name}
          </span>
          {!!badge && (
            <span className="ml-1 px-1.5 py-1 rounded-md bg-secondary text-xs font-semibold text-sidebar-accent-foreground leading-none min-w-[1.25rem] text-center">
              {badge}
            </span>
          )}
        </SidebarMenuButton>
      </div>
    </SidebarMenuItem>
  );
};
